import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Text from 'components/inputs/text';
import Textarea from 'components/inputs/textarea';
import { AttributeType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { removeNullishValues } from 'utils/helpers';
import {
  categoryToProjectSettingCode,
  categoryWeightLabels,
  customerPreferenceAttributeCodes,
  orderedWeightedCategories
} from 'utils/system-gallery-filters';
import { errorModal, successModal } from 'utils/ui';

const PREFERENCE_CODE_SET = new Set<string>(customerPreferenceAttributeCodes);

const CODE_TO_CATEGORY_LABEL: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const cat of orderedWeightedCategories) {
    map[categoryToProjectSettingCode[cat]] = categoryWeightLabels[cat];
  }
  return map;
})();

const CODE_ORDER: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  customerPreferenceAttributeCodes.forEach((code, i) => {
    map[code] = i;
  });
  return map;
})();

export default function CustomerPreferencesPage() {
  const dispatch = useAppDispatch();
  const { attributes } = useAppSelector((state) => state.admin);
  const [editedById, setEditedById] = useState<Record<number, AttributeType>>(
    {}
  );

  const preferenceAttributes = useMemo(
    () =>
      attributes
        .filter((a) => PREFERENCE_CODE_SET.has(a.code))
        .toSorted(
          (a, b) => (CODE_ORDER[a.code] ?? 999) - (CODE_ORDER[b.code] ?? 999)
        ),
    [attributes]
  );

  useEffect(() => {
    setEditedById({});
  }, [attributes]);

  const getRow = (attr: AttributeType): AttributeType =>
    editedById[attr.id] ?? attr;

  const handleChange = (
    attr: AttributeType,
    key: 'label' | 'tooltip',
    value: string
  ) => {
    setEditedById((prev) => ({
      ...prev,
      [attr.id]: {
        ...(prev[attr.id] ?? attr),
        [key]: value
      }
    }));
  };

  const dirtyRows = useMemo(
    () =>
      preferenceAttributes.filter((attr) => {
        const edit = editedById[attr.id];
        if (!edit) return false;
        return (
          (edit.label ?? '') !== (attr.label ?? '') ||
          (edit.tooltip ?? '') !== (attr.tooltip ?? '')
        );
      }),
    [preferenceAttributes, editedById]
  );

  const hasChanges = dirtyRows.length > 0;

  const handleSave = async () => {
    if (!hasChanges) return;
    try {
      await axios.patch(
        'attributes',
        dirtyRows.map((attr) => removeNullishValues(getRow(attr)))
      );
      dispatch(fetchDefaultAdminData());
      successModal(
        `Updated ${dirtyRows.length} preference${
          dirtyRows.length !== 1 ? 's' : ''
        }.`
      );
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    setEditedById({});
  };

  return (
    <div className='mx-auto flex max-w-[1200px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>
        Customer Preferences
      </div>
      <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800'>
        Edit the user-facing label and tooltip for each Customer Preferences
        slider. Tooltips appear when the user hovers the info icon next to the
        slider label.
      </div>
      <div className='flex items-center gap-3'>
        <Button
          text='Save'
          color='green'
          onClick={handleSave}
          disabled={!hasChanges}
        />
        <Button
          text='Reset'
          color='red'
          onClick={handleReset}
          disabled={!hasChanges}
        />
      </div>
      <div className='flex flex-col gap-3'>
        {preferenceAttributes.map((attr) => {
          const row = getRow(attr);
          const dirty = dirtyRows.includes(attr);
          const categoryLabel = CODE_TO_CATEGORY_LABEL[attr.code] ?? attr.code;
          return (
            <div
              key={attr.id}
              className='flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'
            >
              <div className='flex flex-wrap items-center gap-2'>
                <span className='rounded bg-blue-100 px-2 py-0.5 text-sm font-semibold text-blue-700'>
                  {categoryLabel}
                </span>
                <span className='rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600'>
                  {attr.code}
                </span>
                {dirty && (
                  <span className='ml-auto text-xs font-semibold uppercase tracking-wide text-amber-600'>
                    Modified
                  </span>
                )}
              </div>
              <div className='flex flex-col gap-1'>
                <label className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Label
                </label>
                <Text
                  value={row.label}
                  onChange={(value) => handleChange(attr, 'label', value)}
                  placeholder='Label...'
                />
              </div>
              <div className='flex flex-col gap-1'>
                <label className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Tooltip
                </label>
                <Textarea
                  value={row.tooltip ?? ''}
                  onChange={(value) => handleChange(attr, 'tooltip', value)}
                  placeholder='Tooltip text shown to the customer...'
                  rows={4}
                  resize
                  maxLength={1000}
                />
                <div className='text-right text-xs text-gray-400'>
                  {(row.tooltip ?? '').length} / 1000
                </div>
              </div>
            </div>
          );
        })}
        {preferenceAttributes.length === 0 && (
          <div className='py-8 text-center text-gray-500'>
            No customer preference attributes found. Run the latest migrations.
          </div>
        )}
      </div>
    </div>
  );
}
